import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Inbox, Send, X, Loader2, Sparkles, RefreshCw, MessageCircleReply, Zap } from "lucide-react";

const ago = (iso) => {
  if (!iso) return "never";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
};

const STATUS_CLS = {
  pending: "bg-slate-100 text-slate-700", sending: "bg-cyan-100 text-cyan-800",
  failed: "bg-rose-100 text-rose-700", skipped: "bg-amber-100 text-amber-800",
};

const OutboxPopover = ({ sl, onSent }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button variant="outline" size="sm" className="h-9 border-emerald-300 text-emerald-700" data-testid="outbox-btn">
        <Inbox className="w-4 h-4 mr-1.5" /> Outbox
        <span className="ml-1.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold px-1.5" data-testid="outbox-count">{sl.pending}</span>
      </Button>
    </PopoverTrigger>
    <PopoverContent align="end" className="w-[360px] p-0" data-testid="outbox-panel">
      <div className="px-3 py-2 border-b text-[12px] text-slate-600">
        <b className="text-slate-900">Approved texts</b> ·{" "}
        {sl.meta.auto_send && sl.meta.live ? "Auto-send on — fires within ~5 min (8am–8pm Mon–Sat)" : "Fires when you hit Send all"}
      </div>
      <div className="max-h-72 overflow-y-auto divide-y">
        {sl.outbox.length === 0 && <div className="p-4 text-center text-[12px] text-slate-400" data-testid="outbox-empty">Nothing waiting. Add drafts from any card.</div>}
        {sl.outbox.map((o) => (
          <div key={o.lead_id} className="px-3 py-2 text-[12px]" data-testid={`outbox-item-${o.lead_id}`}>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-800 truncate">{o.name}</span>
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_CLS[o.status] || ""}`}>
                {o.status === "sending" && <Loader2 className="inline w-3 h-3 mr-0.5 animate-spin" />}{o.status}
              </span>
              {o.status !== "sending" && (
                <button className="ml-auto text-slate-400 hover:text-rose-600" onClick={() => sl.removeFromOutbox(o.lead_id)} data-testid={`outbox-remove-${o.lead_id}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <p className="text-slate-500 line-clamp-2 mt-0.5">{o.message}</p>
            {o.error && <p className="text-rose-600 mt-0.5">{o.error}</p>}
          </div>
        ))}
      </div>
      <div className="p-2 border-t flex justify-end">
        <Button size="sm" className="bg-gradient-to-r from-emerald-500 to-emerald-600" disabled={!sl.outbox.some((o) => ["pending", "failed"].includes(o.status)) || !!sl.busy}
          onClick={() => sl.sendOutbox(onSent)} data-testid="outbox-send-all-btn">
          <Send className="w-4 h-4 mr-1.5" /> {sl.meta.live ? "Send all now" : "Send all (dry run)"}
        </Button>
      </div>
    </PopoverContent>
  </Popover>
);

// AI controls strip for the Follow-Up Queue header.
export const SmartQueueBar = ({ sl, onSent }) => (
  <div className="flex items-center gap-2 flex-wrap rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-white px-3 py-2" data-testid="smart-queue-bar">
    <Sparkles className="w-4 h-4 text-amber-500" />
    <span className="text-[12px] text-slate-700" data-testid="ai-ranked-at">
      AI ranked <b>{ago(sl.meta.computed_at)}</b>
    </span>
    <Button size="sm" variant="ghost" className="h-7 text-[12px]" disabled={!!sl.busy} onClick={sl.rerank} data-testid="ai-rerank-btn">
      {sl.busy === "rank" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}Re-rank
    </Button>
    <span className="text-slate-300">|</span>
    <span className="text-[12px] text-slate-700" data-testid="reply-checked-at" title={sl.meta.last_reply_check_summary || ""}>
      <MessageCircleReply className="inline w-3.5 h-3.5 text-rose-500 mr-1" />Replies checked <b>{ago(sl.meta.last_reply_check_at)}</b>
    </span>
    <Button size="sm" variant="ghost" className="h-7 text-[12px]" disabled={!!sl.busy} onClick={sl.checkReplies} data-testid="check-replies-btn">
      {sl.busy === "check" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}Check now
    </Button>
    <div className="ml-auto flex items-center gap-2">
      <Badge className={sl.meta.live ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"} data-testid="smart-mode-badge">
        {sl.meta.live ? <><Zap className="w-3 h-3 mr-1" />Live{sl.meta.auto_send ? " · auto-send" : ""}</> : "Dry run"}
      </Badge>
      <OutboxPopover sl={sl} onSent={onSent} />
    </div>
  </div>
);
